/**
 * Draft-first on purpose: this is a MODAL. An overlay is dismissed, not
 * navigated away from, and the dirty guard is a router-level concern — a
 * Keep/Discard prompt cannot fire for something that has no route.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';

export function SaveViewModal({
  open,
  onClose,
  query,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  query: string;
  onSaved: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'private' | 'org'>('private');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => apiSend<{ id: string }>('/api/views', 'POST', { name: name.trim(), scope, query }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error);
      void queryClient.invalidateQueries({ queryKey: qk.views });
      onSaved(res.body!.id);
      setName('');
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Save view">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Urgent unassigned"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Who sees it</label>
          <Select value={scope} onChange={(e) => setScope(e.target.value as 'private' | 'org')}>
            <option value="private">Just me</option>
            <option value="org">Everyone here</option>
          </Select>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {/* Always enabled on purpose: this modal CREATES a view — there is no
              stored record to be dirty against, so validity IS the correct
              guard. The dirty concept applies to editing, not naming. */}
          <Button disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save view'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
